import { getCurrentSession } from './auth.ts';
import { applyTenantRequestHeaders } from './tenantRequestContext';
import { isTransientFetchError, isTransientHttpStatus, sleep } from './requestAbort';

export type BillingPlan = 'monthly' | 'yearly';

export type BillingAccessState = {
  ok: true;
  configured: boolean;
  hasAccess: boolean;
  subscriptionStatus: string;
  status?: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  current_period_end?: string | null;
  trialStart?: string | null;
  trial_start?: string | null;
  trialStartsAt?: string | null;
  trialEnd?: string | null;
  trial_end?: string | null;
  trialEndsAt?: string | null;
  trialUsedAt?: string | null;
  trial_used_at?: string | null;
  invoicePdfUrl?: string | null;
  customerPortalEligible: boolean;
  checkoutEligible: boolean;
  isAdminBillingBypass?: boolean;
  isComplimentaryPartnerAccess?: boolean;
  plan: {
    name: string;
    interval: string;
  };
};

export type AppIdentity = {
  uid: string;
  email?: string | null;
};

const BILLING_STATUS_REQUEST_TIMEOUT_MS = Number(
  import.meta.env?.VITE_BILLING_STATUS_TIMEOUT_MS || import.meta.env?.VITE_WORKSPACE_LOAD_TIMEOUT_MS || 8000,
);

const checkoutSessionRequestCache = new Map<string, Promise<string>>();

const tpStripeFlashLog = (event: string, payload: Record<string, unknown> = {}) => {
  try {
    const monoTs = typeof performance !== 'undefined' ? performance.now() : null;
    console.info('[TP-STRIPE-FLASH]', event, { ts: Date.now(), monoTs, ...payload });
  } catch {}
};

export function getStoredAppIdentity(): AppIdentity | null {
  const parse = (raw: string | null) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const uid = String(parsed?.uid || '').trim();
      if (!uid) return null;
      return {
        uid,
        email: parsed?.email ? String(parsed.email) : null,
      };
    } catch {
      return null;
    }
  };

  return parse(localStorage.getItem('tt_session_user')) || parse(localStorage.getItem('tt_mock_user'));
}

export function buildBillingHeaders(identity: AppIdentity | null) {
  if (!identity?.uid) return {};
  const headers: Record<string, string> = {
    'x-user-id': identity.uid,
  };
  if (identity.email) headers['x-user-email'] = identity.email;
  return headers;
}

async function readJson<T>(res: Response): Promise<T> {
  // Use a cloned response to avoid consuming the original response body
  // when tests or calling code may share the same Response instance.
  const cloned = typeof res.clone === 'function' ? res.clone() : res;
  const body = await cloned.json();
  if (!res.ok) {
    throw new Error(body?.error || 'request_failed');
  }
  return body as T;
}

function buildTimeoutError(label: string, timeoutMs: number) {
  const error = new Error(`${label} request timed out after ${timeoutMs}ms.`);
  error.name = 'TimeoutError';
  return error;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number, label: string): Promise<Response> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    const request = fetch(input, controller ? { ...init, signal: controller.signal } : init);
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        try {
          controller?.abort();
        } catch {}
        reject(buildTimeoutError(label, timeoutMs));
      }, timeoutMs);
    });

    return await Promise.race([request, timeout]);
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw buildTimeoutError(label, timeoutMs);
    }
    throw err;
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
}

// Bounded automatic retry for TRANSIENT billing-status failures (network drop,
// request timeout, 5xx, 429) so a momentary blip during login never surfaces the
// fatal workspace screen. Deterministic 4xx responses and aborts are NOT retried:
// they are returned/thrown immediately exactly as before.
const BILLING_STATUS_MAX_ATTEMPTS = 3; // 1 initial + 2 internal retries
const BILLING_STATUS_RETRY_BASE_DELAY_MS = 300;

async function fetchBillingResponseWithRetry(path: string, init: RequestInit, label: string): Promise<Response> {
  const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const correlationId = String((init.headers as Record<string, string> | undefined)?.['x-tp-bootstrap-id'] || '').trim() || null;
  for (let attempt = 1; ; attempt += 1) {
    try {
      const res = await fetchWithTimeout(path, init, BILLING_STATUS_REQUEST_TIMEOUT_MS, label);
      if (res.ok || !isTransientHttpStatus(res.status) || attempt >= BILLING_STATUS_MAX_ATTEMPTS) {
        tpStripeFlashLog('client_billing_status_finish', {
          correlationId,
          attempt,
          retryOccurred: attempt > 1,
          durationMs: Number(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt).toFixed(1)),
          success: res.ok,
        });
        return res;
      }
      // Transient 5xx / 429: release the response body before retrying.
      try { await res.body?.cancel?.(); } catch {}
      await sleep(BILLING_STATUS_RETRY_BASE_DELAY_MS * attempt);
    } catch (error) {
      if (attempt >= BILLING_STATUS_MAX_ATTEMPTS || !isTransientFetchError(error)) {
        tpStripeFlashLog('client_billing_status_finish', {
          correlationId,
          attempt,
          retryOccurred: attempt > 1,
          durationMs: Number(((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt).toFixed(1)),
          success: false,
          failureCategory: String((error as { name?: string })?.name || '').toLowerCase() === 'aborterror' ? 'aborted' : 'request_error',
        });
        throw error;
      }
      await sleep(BILLING_STATUS_RETRY_BASE_DELAY_MS * attempt);
    }
  }
}

async function buildBillingAuthHeaders(identity: AppIdentity | null, accessTokenOverride?: string) {
  const overrideToken = String(accessTokenOverride || '').trim();
  if (overrideToken) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${overrideToken}`,
    };
    return applyTenantRequestHeaders(headers);
  }

  if (!identity?.uid) {
    const headers: Record<string, string> = {};
    return applyTenantRequestHeaders(headers);
  }

  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) throw new Error('missing_access_token');

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  return applyTenantRequestHeaders(headers);
}

function buildCheckoutSessionCacheKey(
  identity: AppIdentity | null,
  plan: BillingPlan,
  accessTokenOverride?: string,
) {
  const overrideToken = String(accessTokenOverride || '').trim();
  return [
    String(identity?.uid || '').trim(),
    String(identity?.email || '').trim(),
    plan,
    overrideToken ? `override:${overrideToken}` : 'session',
  ].join('|');
}

function readCheckoutReturnSessionId() {
  try {
    const params = new URLSearchParams(window.location.search);
    return String(params.get('session_id') || '').trim();
  } catch {
    return '';
  }
}

export async function fetchBillingStatus(
  identity: AppIdentity | null,
  options?: { tenantSlug?: string | null; correlationId?: string },
): Promise<BillingAccessState> {
  tpStripeFlashLog('billing_helper_enter', {
    kind: 'status_v2',
    uid: identity?.uid || null,
    email: identity?.email || null,
    tenantSlug: String(options?.tenantSlug || '').trim() || null,
  });
  const tenantSlug = String(options?.tenantSlug || '').trim();
  const query = tenantSlug ? `?tenantSlug=${encodeURIComponent(tenantSlug)}` : '';
  const res = await fetchBillingResponseWithRetry(
    `/api/billing/status-v2${query}`,
    {
      headers: {
        ...(await buildBillingAuthHeaders(identity)),
        ...(options?.correlationId ? { 'x-tp-bootstrap-id': options.correlationId } : {}),
      },
    },
    'Billing status',
  );
  const body = await readJson<BillingAccessState>(res);
  tpStripeFlashLog('billing_helper_exit', {
    kind: 'status_v2',
    uid: identity?.uid || null,
    tenantSlug: String(options?.tenantSlug || '').trim() || null,
    configured: body.configured,
    hasAccess: body.hasAccess,
    subscriptionStatus: body.subscriptionStatus,
    checkoutEligible: body.checkoutEligible,
    customerPortalEligible: body.customerPortalEligible,
    hasAccessDecision: body.hasAccess === true,
  });
  return body;
}

export async function fetchCheckoutReturnBillingStatus(identity: AppIdentity | null): Promise<BillingAccessState> {
  const sessionId = readCheckoutReturnSessionId();
  const authHeaders = await buildBillingAuthHeaders(identity);
  tpStripeFlashLog('billing_helper_enter', {
    kind: 'checkout_return',
    uid: identity?.uid || null,
    email: identity?.email || null,
    hasSessionId: !!sessionId,
  });
  const res = await fetchBillingResponseWithRetry(
    sessionId ? '/api/billing/confirm' : '/api/billing/status',
    {
      method: sessionId ? 'POST' : 'GET',
      headers: sessionId
        ? {
            ...authHeaders,
            'Content-Type': 'application/json',
          }
        : authHeaders,
      ...(sessionId
        ? {
            body: JSON.stringify({ sessionId }),
          }
        : {}),
    },
    'Checkout return billing status',
  );
  const body = await readJson<BillingAccessState>(res);
  tpStripeFlashLog('billing_helper_exit', {
    kind: 'checkout_return',
    uid: identity?.uid || null,
    configured: body.configured,
    hasAccess: body.hasAccess,
    subscriptionStatus: body.subscriptionStatus,
    checkoutEligible: body.checkoutEligible,
    customerPortalEligible: body.customerPortalEligible,
    hasAccessDecision: body.hasAccess === true,
  });
  return body;
}

export async function createCheckoutSession(
  identity: AppIdentity | null,
  options?: {
    plan?: BillingPlan;
    accessToken?: string;
  },
): Promise<string> {
  const plan = options?.plan === 'yearly' ? 'yearly' : 'monthly';
  const cacheKey = buildCheckoutSessionCacheKey(identity, plan, options?.accessToken);
  const inFlight = checkoutSessionRequestCache.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  tpStripeFlashLog('checkout_session_helper_enter', {
    uid: identity?.uid || null,
    email: identity?.email || null,
    plan,
    hasAccessTokenOverride: !!String(options?.accessToken || '').trim(),
  });
  const requestPromise = (async () => {
    const res = await fetch('/api/billing/checkout-session', {
      method: 'POST',
      headers: {
        ...(await buildBillingAuthHeaders(identity, options?.accessToken)),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan }),
    });
    const body = await readJson<{ ok: true; url: string }>(res);
    tpStripeFlashLog('checkout_session_helper_exit', {
      uid: identity?.uid || null,
      plan,
      hasUrl: !!body.url,
      checkoutUrlHost: (() => {
        try { return new URL(body.url).host; } catch { return null; }
      })(),
    });
    return body.url;
  })();

  checkoutSessionRequestCache.set(cacheKey, requestPromise);
  void requestPromise.finally(() => {
    if (checkoutSessionRequestCache.get(cacheKey) === requestPromise) {
      checkoutSessionRequestCache.delete(cacheKey);
    }
  }).catch(() => undefined);

  return requestPromise;
}

export async function createPortalSession(identity: AppIdentity | null): Promise<string> {
  const res = await fetch('/api/billing/portal-session', {
    method: 'POST',
    headers: await buildBillingAuthHeaders(identity),
  });
  const body = await readJson<{ ok: true; url: string }>(res);
  return body.url;
}

export async function createPendingCheckoutSession(
  email: string,
  username: string,
  plan: BillingPlan = 'monthly',
  tenantSlug?: string,
  password?: string
): Promise<string> {
  const res = await fetch('/api/billing/pending-checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, username, plan, password, ...(tenantSlug ? { tenantSlug } : {}) }),
  });
  const body = await res.json().catch(() => ({})) as {
    ok?: boolean;
    url?: string;
    error?: string;
    code?: string;
    detail?: string;
    message?: string;
    pendingSignupId?: string;
  };
  if (!res.ok) {
    const err = new Error(String(body.detail || body.message || body.error || 'Unable to start checkout.').trim() || 'Unable to start checkout.') as Error & { code?: string; status?: number };
    err.code = String(body.code || body.error || '').trim().toLowerCase() || undefined;
    err.status = res.status;
    throw err;
  }
  if (!body.url) {
    throw new Error('Checkout did not return a payment URL. Please try again.');
  }
  if (body.pendingSignupId) {
    try { sessionStorage.setItem('tt_pending_signup_id', body.pendingSignupId); } catch {}
  }
  return body.url;
}

export type PendingCheckoutStatus = {
  status: string;
  checkoutCompleted: boolean;
  provisioningInProgress: boolean;
  provisioningFailed: boolean;
  complete: boolean;
};

export async function fetchPendingCheckoutStatus(email: string): Promise<PendingCheckoutStatus | null> {
  let id = '';
  try { id = sessionStorage.getItem('tt_pending_signup_id') || ''; } catch {}
  if (!id || !email) return null;
  try {
    const res = await fetch(`/api/billing/pending-checkout-status?pending_signup_id=${encodeURIComponent(id)}&email=${encodeURIComponent(email)}`);
    if (!res.ok) return null;
    const body = await res.json();
    return body?.ok ? body as PendingCheckoutStatus : null;
  } catch { return null; }
}

export async function pollCheckoutSuccessStatus(email: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/billing/checkout-success-status?email=${encodeURIComponent(email)}`);
    const body = await res.json();
    return !!body?.ready;
  } catch {
    return false;
  }
}
